const _ = require('lodash');

const { BYTES_PER_ELEMENT } = Uint32Array;
const MAX_32_UINT = 2 ** 32 - 1;
const SIGNIFICANT_DIGITS = 3;
const SIGNIFICANT_DIGITS_MULTIPLIER = 10 ** SIGNIFICANT_DIGITS;

/**
 * Converts a heightMap into a Buffer object which can be easily stored
 * @param {Object} heightMap The heightMap object to serialize
 * @param {Number} bufferVersion The version of the buffer format to use
 * @returns {Buffer}
 */
exports.heightMapToBuffer = function (heightMap, bufferVersion = 1) {
  // Format is HEADER: TOTAL_HEADER_BYTE_LENGTH, HEADER_VALUES, HEIGHT_MAP_DATA
  const header = [];
  // Just adding a version number for future-proofing
  header.push(bufferVersion);
  const heightMapResolution = heightMap.length;
  header.push(heightMapResolution);
  // Add the total length of the header to the start
  header.unshift(BYTES_PER_ELEMENT * (header.length + 1));

  const data = _.flatMapDeep(heightMap, (row) => row.map((height) => {
    // Serialize null to MAX_32_UINT
    if (height === null) {
      return MAX_32_UINT;
    }

    // Store Ms as mms
    return Math.round(height * SIGNIFICANT_DIGITS_MULTIPLIER);
  }));

  const combinedHeightMapArray = header.concat(data);
  const typedArray = new Uint32Array(combinedHeightMapArray);
  return Buffer.from(typedArray.buffer);
};

/**
 * Converts a Buffer object composed of UInt32s into a heightMap object
 * @param {Buffer} buffer The binary data of a file representing a heightMap
 * @returns {Object}
 */
exports.bufferToHeightMap = function (buffer) {
  // This little double conversion needs to happen or readArray will contain values maxed out at 2^8
  const bufferAsTypedIntArray = new Uint8Array(buffer);
  return exports.uInt8ArrayToHeightMap(bufferAsTypedIntArray);
};

/**
 * Converts a UInt8Array into a heightMap object
 * @param {Uint8Array} uInt8Array The binary data of a file representing a heightMap
 * @returns {Object}
 */
exports.uInt8ArrayToHeightMap = function (uInt8Array) {
  // Need to calculate this manually, as JS will set the Uint32Arrays length to be the same size
  // as the buffer, but with values of type U32Int, resulting in many 0s as trailing padding
  const readArrayLength = uInt8Array.length / BYTES_PER_ELEMENT;
  const typedArray = new Uint32Array(uInt8Array.buffer);
  // Switch to a regular array so we can parse MAX_32_UINT -> null without it being cast to 0
  const untypedArray = Array.from(typedArray).slice(0, readArrayLength);

  // Parse the header
  const headerLength = untypedArray[0];
  const header = untypedArray.slice(0, headerLength / BYTES_PER_ELEMENT);
  // eslint-disable-next-line no-unused-vars
  const [_headerLength, _heightMapVersion, heightMapResolution] = header;

  // Parse out the contents of the body
  const heightMapData = untypedArray
    .slice(headerLength / BYTES_PER_ELEMENT)
    .map(value => value === MAX_32_UINT ? null : value / SIGNIFICANT_DIGITS_MULTIPLIER);

  return {
    heightMap: _.chunk(heightMapData, heightMapResolution),
    header,
  };
};
